import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'

import { Form, Modal, Link, Button, Column } from '../../../..'
import { DEFAULT_IMAGE_LENGTH } from '../../../../../reducers/content/initialState'
import { promoForm as promoFormProps } from '../../../../../types'
import FormWrapper from './FormWrapper'
import { withTranslation } from 'react-i18next'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import * as ContentActions from '../../../../../actions/content'
import { ContentTypes } from '../../../../../constants'
import { FileUtils } from '../../../../../utils'
import promoValidate from './promoValidation'
import { ObjectUtils } from '../../../../../utils'
import PromoList from './PromoList'
import { FooterStyle } from './PromoItem.style'

const { FormHeader, FormGroup } = Form

// Default start date
const defaultStartDateDate = new Date()
defaultStartDateDate.setHours(0)
defaultStartDateDate.setMinutes(0)
defaultStartDateDate.setSeconds(0)
const defaultStartDate = defaultStartDateDate.getTime()

// Default end date
const defaultEndDateDate = new Date()
defaultEndDateDate.setDate(new Date().getDate())
defaultEndDateDate.setHours(24)
defaultEndDateDate.setMinutes(0)
defaultEndDateDate.setSeconds(0)
const defaultEndDate = defaultEndDateDate.getTime() + 1000 * 60 * 60 * 24 * 30

class PromosForm extends PureComponent {
    static propTypes = {
        editFormValue: PropTypes.func.isRequired,
        editForm: PropTypes.func.isRequired,
        promoForm: PropTypes.shape(promoFormProps).isRequired,
        errors: PropTypes.shape({
            name: PropTypes.string,
            file: PropTypes.string,
            selected_media: PropTypes.string,
            date_start: PropTypes.string,
            date_end: PropTypes.string,
            length: PropTypes.string,
        }),
        innerRef: PropTypes.shape({}),
        isOpen: PropTypes.bool,
        isWorking: PropTypes.bool,
        values: PropTypes.shape(promoFormProps),
        onChangeForm: PropTypes.shape({
            name: PropTypes.func,
            file: PropTypes.func,
            date_start: PropTypes.func,
            date_end: PropTypes.func,
            metadata: PropTypes.func,
            selected_media: PropTypes.func,
        }),
        onClickCancel: PropTypes.func,
        media: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        channelName: PropTypes.string,
        channelId: PropTypes.number,
        channelPromos: PropTypes.arrayOf(PropTypes.shape({})),
        selectedChannel: PropTypes.shape({}),
        attachMediaToChannel: PropTypes.func.isRequired,
        fetchChannelWithDevices: PropTypes.func.isRequired,
        onClickSubmit: PropTypes.func,
        onDropMedia: PropTypes.func,
        t: PropTypes.func.isRequired,
        fetchMedia: PropTypes.func.isRequired,
        reloadPromo: PropTypes.func.isRequired,
        devices: PropTypes.arrayOf(PropTypes.shape({})),
    }

    static defaultProps = {
        errors: {
            name: null,
            file: null,
            date_start: null,
            date_end: null,
        },
        innerRef: null,
        isOpen: false,
        isWorking: false,
        values: {},
        onChangeForm: {
            name: () => null,
            media: () => null,
            date_start: () => null,
            date_end: () => null,
            length: () => null,
            selected_media: () => null,
        },
        onClickCancel: () => null,
        onClickSubmit: () => null,
        reloadPromo: () => null,
    }

    constructor(props) {
        super(props)
        this.state = {
            promoId: 0,
            promoFormErrors: {
                name: null,
                file: null,
                date_start: null,
                date_end: null,
            },
            progress: 0,
        }
        this.onClickNullStartDate = this.onClickNullStartDate.bind(this)
        this.onClickNullEndDate = this.onClickNullEndDate.bind(this)
        this.onClickRemoveStartDate = this.onClickRemoveStartDate.bind(this)
        this.onClickRemoveEndDate = this.onClickRemoveEndDate.bind(this)
        this.onChangePromoFormValue = this.onChangePromoFormValue.bind(this)
        this.onChangeFormFile = this.onChangeFormFile.bind(this)
    }

    componentDidMount() {
        this.loadData()
    }

    loadData() {
        const { resetUpload } = this.props
        resetUpload(ContentTypes.MEDIA)
        this.setState({ progress: 0 })
    }

    /**
     * If the selected media is an image
     */
    get isFileImage() {
        const { type = '' } = this.file || {}
        return type.includes('image')
    }
    /**
     * If the selected media is a video
     */
    get isFileVideo() {
        const { type = '' } = this.file || {}
        return type.includes('video')
    }
    /**
     * Progress in 100 per cent
     */
    get progress() {
        const { progress } = this.state
        return Math.round(progress * 100)
    }
    /**
     * Video length needs to be calculated after rendering the video in html,
     * so this function is triggered when the <video> tag finishes loading the metadata
     */
    onVideoMetadataChange = (e) => {
        if (!this.hasFile || !this.isFileVideo) {
            return null
        }

        // Get metadata
        const {
            target: { videoHeight, videoWidth, duration },
        } = e

        const { onChangeForm } = this.props
        const { metadata: onChangeMetadata } = onChangeForm
        const videoLength = duration * 1000 // Set to ms

        const metadata = { videoHeight, videoLength, videoWidth }

        // If onChangeMetadata function exists
        if (onChangeMetadata instanceof Function) {
            onChangeMetadata(metadata)
        }
    }
    /**
     * If the file selected to upload is an image, we should trigger the onChangeLength event
     * with the default length
     */
    innerOnChangeFile = (...args) => {
        const { onChangeForm } = this.props
        const { length: onChangeLength, file: onChangeFile } = onChangeForm

        if (onChangeFile instanceof Function) {
            onChangeFile(...args)
        }

        // If file has been set, and it's an image, send the default image length
        if (this.hasFile && this.isFileImage && onChangeLength instanceof Function) {
            onChangeLength({
                target: { name: 'length', value: DEFAULT_IMAGE_LENGTH },
            })
        }
    }

    /**
     * Render Start Date label
     */
    renderStartDateLabel() {
        const { onClickRemoveStartDate, t } = this.props

        return (
            <Fragment>
                {t('date-start')}
                &nbsp;
                <Link onClick={onClickRemoveStartDate}>&times;</Link>
            </Fragment>
        )
    }

    /**
     * Render End label
     */
    renderEndDateLabel() {
        const { onClickRemoveEndDate, t } = this.props

        return (
            <Fragment>
                {t('date-end')}
                &nbsp;
                <Link onClick={onClickRemoveEndDate}>&times;</Link>
            </Fragment>
        )
    }

    /**
     * Get file object for selected media to upload
     */
    get file() {
        const { values } = this.props
        const { promoId } = this.state
        const { file } = values[promoId] || {}
        return file[0]
    }

    onChangeFormFile({ target: { name, files } }) {
        const { editFormValue, promoForm } = this.props
        editFormValue('promo', { name, value: files })

        const { name: formFileName } = promoForm

        if (!formFileName && files.length) {
            const { name: fileName } = files[0]
            const fileWithoutExtension = FileUtils.getFileWithoutExtension(fileName)
            editFormValue('promo', { name: 'name', value: fileWithoutExtension })
        }
    }

    buildOnchangeObjectPromos() {
        return {
            file: this.onChangeFormFile,
            selected_media: (value) => this.onChangePromoFormValue({ target: { name: 'selected_media', value } }),
            name: this.onChangePromoFormValue,
            date_start: (value) => this.onChangePromoFormValue({ target: { name: 'date_start', value } }),
            date_end: (value) => this.onChangePromoFormValue({ target: { name: 'date_end', value } }),
            metadata: ({ videoHeight, videoLength, videoWidth }) => {
                this.onChangePromoFormValue({
                    target: { name: 'height', value: videoHeight },
                })
                this.onChangePromoFormValue({
                    target: { name: 'length', value: videoLength },
                })
                this.onChangePromoFormValue({
                    target: { name: 'width', value: videoWidth },
                })
            },
        }
    }

    onChangePromoFormValue({ target: { name, value } }) {
        const { editFormValue } = this.props
        editFormValue('promo', { name, value })
    }

    /**
     * Form Validation
     */
    validatePromo() {
        let { promoForm } = this.props
        const formErrors = promoValidate(promoForm)
        this.setState({ formErrors })
        return ObjectUtils.isObjValuesEmpty(formErrors)
    }

    promoIsActive(media) {
        const currentDate = Date.now()
        return media.date_start < currentDate && (media.date_end > currentDate || media.date_end == null)
    }

    onSavePromo = async (mediaId) => {
        this.setState({ isWorking: true })

        try {
            const { attachMediaToChannel, fetchChannelWithDevices, channelId, media } = this.props
            const mediaItem = media.find((item) => mediaId === item.id)
            await attachMediaToChannel(channelId, mediaId)
            await this.restartDevicesApp(this.promoIsActive(mediaItem))
            await fetchChannelWithDevices(channelId)
        } catch (e) {
            console.error(e)
        } finally {
            const { onClickSubmit } = this.props
            onClickSubmit()
            this.setState({ isWorking: false })
        }
    }

    onUploadPromo = async () => {
        if (!this.validatePromo()) {
            return
        }

        this.setState({ isWorking: true })

        try {
            const { promoForm, uploadMedia, attachMediaToChannel, fetchChannelWithDevices, channelId } = this.props
            const { file } = promoForm
            const { size } = file[0]
            const media = { ...promoForm, size }
            const saved = await uploadMedia(file[0], media, {
                onProgress: ({ progress }) => this.setState({ progress }),
            })

            if (saved) {
                await attachMediaToChannel(channelId, saved.id)
                await this.restartDevicesApp(this.promoIsActive(media))
                await fetchChannelWithDevices(channelId)
            }

            this.loadData()
        } catch (e) {
            console.error(e)
        } finally {
            const { onClickSubmit } = this.props
            onClickSubmit()
            this.setState({ isWorking: false })
        }
    }

    onClickNullStartDate() {
        this.onChangePromoFormValue({
            target: { name: 'date_start', value: defaultStartDate },
        })
    }

    onClickNullEndDate() {
        this.onChangePromoFormValue({
            target: { name: 'date_end', value: defaultEndDate },
        })
    }

    onClickRemoveStartDate() {
        this.onChangePromoFormValue({
            target: { name: 'date_start', value: null },
        })
    }

    onClickRemoveEndDate() {
        this.onChangePromoFormValue({
            target: { name: 'date_end', value: null },
        })
    }

    dropMedia = async (media) => {
        const { onDropMedia } = this.props
        onDropMedia(media)
        await this.restartDevicesApp()
    }

    promoDisplay(item) {
        const { name } = item || {}
        return `${name} `
    }

    splitPromos(promos) {
        const currentDate = Date.now()
        const [active, scheduled] = promos.reduce(
            (result, element) => {
                result[element.date_start < currentDate && (element.date_end > currentDate || element.date_end == null) ? 0 : 1].push(element)
                return result
            },
            [[], []]
        )
        return { active, scheduled }
    }

    onFinishSubmit = async () => {
        const { fetchChannelWithDevices, channelId, onClickSubmit } = this.props
        await fetchChannelWithDevices(channelId)
        await this.restartDevicesApp()
        onClickSubmit()
    }

    /**
     * Fetches new suggestions for the media input
     * @param {string} name Name written on the input
     */
    onTextChangeMedia = (name) => {
        const { fetchMedia } = this.props
        const { textMedia } = this.state

        if (name !== textMedia) {
            fetchMedia(null, { name })
            this.setState({ textMedia: name })
        }
    }

    restartDevicesApp = async (withNotification = false) => {
        const { devices, reloadPromo } = this.props
        try {
            await Promise.all(
                devices?.map(async ({ id }, index) => {
                    setTimeout(async () => {
                        if (id) {
                            reloadPromo(id, withNotification)
                        }
                    }, index * 1000)
                })
            )
        } catch (e) {
            console.error(e)
        }
    }

    render() {
        const { onClickCancel, media, channelName, promoFormErrors, promoForm, selectedChannel, isOpen, channelId, t, devices, reloadPromo } = this.props
        const { isWorking } = this.state
        const { active, scheduled } =
            selectedChannel && selectedChannel.media && selectedChannel?.name === channelName ? this.splitPromos(selectedChannel.media) : {}

        return (
            <Modal isOpen={isOpen} onClose={onClickCancel} scrollable>
                <Form>
                    <FormHeader>{t('manage promo', { channelName })}</FormHeader>
                    {selectedChannel && selectedChannel.media && selectedChannel?.name === channelName && (
                        <>
                            <FormGroup title={`${active.length} Active Promos:`}>
                                <div class="promo-list">
                                    <PromoList
                                        promos={active}
                                        onDropMedia={this.dropMedia}
                                        onFinishSubmit={this.onFinishSubmit}
                                        channelId={channelId}
                                        devices={devices}
                                        reloadPromo={reloadPromo}
                                    />
                                </div>
                            </FormGroup>
                        </>
                    )}
                    {selectedChannel && selectedChannel.media && selectedChannel?.name === channelName && (
                        <>
                            <FormGroup title={`${scheduled.length} Scheduled Promos:`}>
                                <div class="promo-list">
                                    <PromoList
                                        promos={scheduled}
                                        onDropMedia={this.dropMedia}
                                        onFinishSubmit={this.onFinishSubmit}
                                        channelId={channelId}
                                        devices={devices}
                                        reloadPromo={reloadPromo}
                                    />
                                </div>
                            </FormGroup>
                        </>
                    )}
                    {selectedChannel && selectedChannel.media && selectedChannel?.name === channelName && (
                        <FormWrapper
                            errors={promoFormErrors}
                            onChangeForm={this.buildOnchangeObjectPromos()}
                            values={promoForm}
                            t={t}
                            onClickCancel={() => this.setState({ isOpen: false })}
                            onClickSave={this.onSavePromo}
                            onClickUpload={this.onUploadPromo}
                            media={media}
                            onClickNullStartDate={this.onClickNullStartDate}
                            onClickNullEndDate={this.onClickNullEndDate}
                            onClickRemoveStartDate={this.onClickRemoveStartDate}
                            onClickRemoveEndDate={this.onClickRemoveEndDate}
                            onTextChangeMedia={this.onTextChangeMedia}
                        />
                    )}
                    <FormGroup style={FooterStyle}>
                        <Column right width="100%">
                            <Button theme="secondary" onClick={onClickCancel} size="sm">
                                {t('Close')}
                            </Button>
                            <Button theme={isWorking ? 'loading' : 'forestGreen'} onClick={this.onUploadPromo} size="sm" disabled={isWorking}>
                                {isWorking ? `${this.progress} %` : t('Upload')}
                            </Button>
                        </Column>
                    </FormGroup>
                </Form>
            </Modal>
        )
    }
}

const mapStateToProps = ({
    content: {
        form: { promo: promoForm },
        media,
        selected: { channels: selectedChannel },
    },
}) => ({
    promoForm,
    media,
    selectedChannel,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...ContentActions,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PromosForm))
